import gettext from '@eb/gettext';
import { isPromise } from '@eb/eds-uploader';

/**
 * The ConfirmNavigationDialog has significant complications for testing, including
 * the inability to unmount which pollutes the window with onBeforeUnload handlers.
 * Separated this function so it could be tested independently
 */
export const handleSaveAction = (onSave, onDiscard, onClose) => {
    const result = onSave();

    if (!isPromise(result)) {
        // if no promise was returned, save action was not fired
        // (possibly due to form validation failure)
        onClose();
        return null;
    }

    return result.then(onDiscard).catch(onClose);
};

export const getDialogProps = (onDiscard, onClose, onSave) => {
    // if no save function is specified, discard is primary.
    let actions = {
        onPrimaryAction: onDiscard,
        primaryText: gettext('Discard'),
    };
    let message = gettext(
        "You've made changes. Please discard them to continue.",
    );

    if (onSave) {
        // if save function is provided, discard is secondary
        actions = {
            onSecondaryAction: onDiscard,
            secondaryText: gettext('Discard'),
            onPrimaryAction: onSave,
            primaryText: gettext('Save'),
        };
        message = gettext(
            "You've made changes. Please save or discard them to continue.",
        );
    }

    return {
        onClose,
        onClickOutside: onClose,
        title: gettext('Review changes'),
        message,
        secondaryType: 'link',
        primaryType: 'link',
        ...actions,
    };
};

/**
 * ReactRouter provides a mechanism for setting synchronous routeLeaveHooks.
 * This provides an asynchronous version of the same mechanism, useful for
 * performing asynchronous operations before allowing the router to proceed.
 * @example
 * // componentDidMount() {
 * //     let {router, route} = this.props;
 * //     setAsyncRouteLeaveHook(router, route, this.routerWillLeave.bind(this))
 * // }
 * // routerWillLeave(nextLocation) {
 * //     return new Promise((resolve, reject) => {
 * //         if (whatever) {
 * //             resolve(true);
 * //         } else {
 * //             reject();
 * //         }
 * //     });
 * // }
 */
export const setAsyncRouteLeaveHook = (router, route, onRouteLeave) => {
    let withinHandler = false;

    router.setRouteLeaveHook(route, (nextLocation) => {
        if (!withinHandler) {
            const handlerResult = onRouteLeave(nextLocation);

            // if onRouteLeave returns a "thenable" (aka an object with a Promise-like interface),
            // navigation should be cancelled until promise is resolved
            if (handlerResult && typeof handlerResult.then === 'function') {
                handlerResult
                    .then((shouldProceed) => {
                        if (shouldProceed && nextLocation) {
                            withinHandler = true;
                            router.push(nextLocation);
                        }
                        withinHandler = false;
                    })
                    .catch(() => {
                        withinHandler = false;
                    });
                return false;
            }
        }

        return undefined;
    });
};
